<!-- eslint-disable -->
<template>
  <v-container class="friends-container">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-6">
          <div>
            <h1 class="text-h4">{{ $t('friends.title') }}</h1>
            <p class="text-subtitle-1 text-grey">{{ $t('friends.subtitle') }}</p>
          </div>
          <v-btn color="primary" :to="'/users'">
            <v-icon start>mdi-account-search</v-icon>
            {{ $t('friends.findFriends') }}
          </v-btn>
        </div>

        <!-- Tabs -->
        <v-tabs v-model="tab" bg-color="white" color="primary" class="mb-4">
          <v-tab value="friends">
            {{ $t('friends.myFriends') }}
            <v-badge v-if="friendsCount > 0" :content="friendsCount" color="primary" inline class="ml-2"></v-badge>
          </v-tab>
          <v-tab value="pending">
            {{ $t('friends.requests') }}
            <v-badge v-if="pendingCount > 0" :content="pendingCount" color="error" inline class="ml-2"></v-badge>
          </v-tab>
          <v-tab value="sent">
            {{ $t('friends.sent') }}
            <v-badge v-if="sentCount > 0" :content="sentCount" color="grey" inline class="ml-2"></v-badge>
          </v-tab>
        </v-tabs>

        <!-- Loading -->
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          class="d-block mx-auto my-8"
        ></v-progress-circular>

        <v-window v-else v-model="tab">
          <!-- Friends Tab -->
          <v-window-item value="friends">
            <v-card v-if="friends.length > 0" elevation="2">
              <v-list>
                <v-list-item
                  v-for="friend in friends"
                  :key="friend.userId"
                  :to="`/profile/${friend.username}`"
                  class="py-3"
                >
                  <template v-slot:prepend>
                    <div class="position-relative">
                      <v-avatar color="primary" size="48">
                        <v-img v-if="friend.avatarUrl" :src="friend.avatarUrl"></v-img>
                        <span v-else class="text-h6">{{ friend.displayName?.charAt(0) || 'U' }}</span>
                      </v-avatar>
                      <!-- Online status indicator -->
                      <span 
                        v-if="friend.onlineStatus && friend.onlineStatus !== 'hidden'"
                        class="online-indicator"
                        :class="friend.onlineStatus"
                        :title="getStatusText(friend.onlineStatus)"
                      ></span>
                    </div>
                  </template>

                  <v-list-item-title class="font-weight-bold">
                    {{ friend.displayName }}
                    <v-chip 
                      v-if="friend.onlineStatus === 'online'" 
                      size="x-small" 
                      color="success" 
                      class="ml-2"
                    >
                      {{ $t('profile.online') }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    @{{ friend.username }}
                    <span v-if="friend.location" class="ml-2">
                      <v-icon size="small">mdi-map-marker</v-icon>
                      {{ friend.location }}
                    </span>
                  </v-list-item-subtitle>

                  <template v-slot:append>
                    <v-btn
                      icon
                      variant="text"
                      color="error"
                      size="small"
                      @click.prevent="removeFriend(friend)"
                    >
                      <v-icon>mdi-account-remove</v-icon>
                      <v-tooltip activator="parent" location="top">{{ $t('friends.remove') }}</v-tooltip>
                    </v-btn>
                  </template>
                </v-list-item>
              </v-list>
            </v-card>
            <v-alert v-else type="info" variant="tonal">
              {{ $t('friends.noFriends') }}
            </v-alert>
          </v-window-item>

          <!-- Pending Requests Tab -->
          <v-window-item value="pending">
            <v-card v-if="pendingRequests.length > 0" elevation="2">
              <v-list>
                <v-list-item
                  v-for="request in pendingRequests"
                  :key="request.friendshipId"
                  class="py-3"
                >
                  <template v-slot:prepend>
                    <v-avatar 
                      color="primary" 
                      size="48"
                      class="cursor-pointer"
                      @click="$router.push(`/profile/${request.username}`)"
                    >
                      <v-img v-if="request.avatarUrl" :src="request.avatarUrl"></v-img>
                      <span v-else class="text-h6">{{ request.displayName?.charAt(0) || 'U' }}</span>
                    </v-avatar>
                  </template>

                  <v-list-item-title 
                    class="font-weight-bold cursor-pointer"
                    @click="$router.push(`/profile/${request.username}`)"
                  >
                    {{ request.displayName }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    @{{ request.username }} • {{ formatDate(request.requestedAt) }}
                  </v-list-item-subtitle>

                  <template v-slot:append>
                    <v-btn
                      color="primary"
                      size="small"
                      class="mr-2"
                      @click="acceptRequest(request)"
                      :loading="request.accepting"
                    >
                      <v-icon start>mdi-check</v-icon>
                      {{ $t('friends.accept') }}
                    </v-btn>
                    <v-btn
                      variant="outlined"
                      size="small"
                      @click="declineRequest(request)"
                      :loading="request.declining"
                    >
                      {{ $t('friends.decline') }}
                    </v-btn>
                  </template>
                </v-list-item>
              </v-list>
            </v-card>
            <v-alert v-else type="info" variant="tonal">
              {{ $t('friends.noRequests') }}
            </v-alert>
          </v-window-item>

          <!-- Sent Requests Tab -->
          <v-window-item value="sent">
            <v-card v-if="sentRequests.length > 0" elevation="2">
              <v-list>
                <v-list-item
                  v-for="request in sentRequests"
                  :key="request.friendshipId"
                  :to="`/profile/${request.username}`"
                  class="py-3"
                >
                  <template v-slot:prepend>
                    <v-avatar color="primary" size="48">
                      <v-img v-if="request.avatarUrl" :src="request.avatarUrl"></v-img>
                      <span v-else class="text-h6">{{ request.displayName?.charAt(0) || 'U' }}</span>
                    </v-avatar>
                  </template>

                  <v-list-item-title class="font-weight-bold">
                    {{ request.displayName }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    @{{ request.username }} • {{ $t('friends.sentOn') }} {{ formatDate(request.requestedAt) }}
                  </v-list-item-subtitle>

                  <template v-slot:append>
                    <v-btn
                      variant="text"
                      color="error"
                      size="small"
                      @click.prevent="cancelRequest(request)"
                      :loading="request.cancelling"
                    >
                      {{ $t('friends.cancel') }}
                    </v-btn>
                  </template>
                </v-list-item>
              </v-list>
            </v-card>
            <v-alert v-else type="info" variant="tonal">
              {{ $t('friends.noSentRequests') }}
            </v-alert>
          </v-window-item>
        </v-window>
      </v-col>
    </v-row>

    <!-- Confirm Dialog -->
    <confirm-dialog
      ref="confirmDialog"
      :title="confirmTitle"
      :message="confirmMessage"
      :confirmText="$t('common.confirm')"
      :cancelText="$t('common.cancel')"
      confirmColor="error"
      icon="mdi-account-remove"
    />
  </v-container>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import ConfirmDialog from '../components/ConfirmDialog.vue'

const { t } = useI18n()

// Get refresh function from App
const refreshFriendRequests = inject('refreshFriendRequests', () => {})

const tab = ref('friends')
const loading = ref(true)
const friends = ref([])
const pendingRequests = ref([])
const sentRequests = ref([])
const confirmDialog = ref(null)
const confirmTitle = ref('')
const confirmMessage = ref('')

const friendsCount = computed(() => friends.value.length)
const pendingCount = computed(() => pendingRequests.value.length)
const sentCount = computed(() => sentRequests.value.length)

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString()
}

const getStatusText = (status) => {
  switch (status) {
    case 'online': return t('profile.online')
    case 'away': return t('profile.away')
    case 'offline': return t('profile.offline')
    default: return ''
  }
}

const loadFriends = async () => {
  try {
    const response = await api.get('/friends', { params: { status: 'accepted' } })
    friends.value = response.data.friends || []
  } catch (error) {
    console.error('Error loading friends:', error)
  }
}

const loadPendingRequests = async () => {
  try {
    const response = await api.get('/friends', { params: { status: 'pending' } })
    pendingRequests.value = (response.data.friends || []).map(r => ({
      ...r,
      accepting: false,
      declining: false
    }))
  } catch (error) {
    console.error('Error loading pending requests:', error)
  }
}

const loadSentRequests = async () => {
  try {
    const response = await api.get('/friends', { params: { status: 'sent' } })
    sentRequests.value = (response.data.friends || []).map(r => ({
      ...r,
      cancelling: false
    }))
  } catch (error) {
    console.error('Error loading sent requests:', error)
  }
}

const loadAll = async () => {
  loading.value = true
  await Promise.all([
    loadFriends(),
    loadPendingRequests(),
    loadSentRequests()
  ])
  loading.value = false
}

const acceptRequest = async (request) => {
  request.accepting = true
  try {
    const response = await api.put('/friends', {
      friendshipId: request.friendshipId,
      action: 'accept'
    })
    if (response.data.success) {
      await loadAll()
      refreshFriendRequests() // Update nav badge
    }
  } catch (error) {
    console.error('Error accepting request:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    request.accepting = false
  }
}

const declineRequest = async (request) => {
  request.declining = true
  try {
    const response = await api.put('/friends', {
      friendshipId: request.friendshipId,
      action: 'decline'
    })
    if (response.data.success) {
      await loadPendingRequests()
      refreshFriendRequests() // Update nav badge
    }
  } catch (error) {
    console.error('Error declining request:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    request.declining = false
  }
}

const cancelRequest = async (request) => {
  request.cancelling = true
  try {
    const response = await api.delete(`/friends?friendshipId=${request.friendshipId}`)
    if (response.data.success) {
      await loadSentRequests()
    }
  } catch (error) {
    console.error('Error cancelling request:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    request.cancelling = false
  }
}

const removeFriend = async (friend) => {
  confirmTitle.value = t('friends.removeFriend')
  confirmMessage.value = t('friends.removeConfirm', { name: friend.displayName })
  
  const confirmed = await confirmDialog.value.open()
  if (!confirmed) return
  
  try {
    const response = await api.delete(`/friends?userId=${friend.userId}`)
    if (response.data.success) {
      await loadFriends()
    }
  } catch (error) {
    console.error('Error removing friend:', error)
    alert(error.response?.data?.error || t('common.error'))
  }
}

onMounted(() => {
  loadAll()
})
</script>

<style scoped>
.friends-container {
  max-width: 900px;
  margin: 0 auto;
  padding-top: 2rem;
}

.cursor-pointer {
  cursor: pointer;
}

.position-relative {
  position: relative;
}

.online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid white;
}

.online-indicator.online {
  background-color: #4CAF50;
}

.online-indicator.away {
  background-color: #FFC107;
}

.online-indicator.offline {
  background-color: #9E9E9E;
}
</style>